'use client'

import { useCallback, useEffect, useId, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { loadLanguageDict, useI18n } from './i18n'

export const LANGUAGE_OPTIONS = Object.freeze([
  { lang: 'en', tag: 'EN', label: 'English', flag: '/leng/en.png', dir: 'ltr' },
  { lang: 'zh', tag: '中文', label: '中文', flag: '/leng/zh.png', dir: 'ltr' },
  { lang: 'uk', tag: 'UA', label: 'Українська', flag: '/leng/uk.png', dir: 'ltr' },
  { lang: 'ru', tag: 'RU', label: 'Русский', flag: '/leng/ru.png', dir: 'ltr' },
  { lang: 'ar', tag: 'AR', label: 'العربية', flag: '/leng/ar.png', dir: 'rtl' },
  { lang: 'tr', tag: 'TR', label: 'Türkçe', flag: '/leng/tr.png', dir: 'ltr' },
  { lang: 'es', tag: 'ES', label: 'Español', flag: '/leng/es.png', dir: 'ltr' },
])

const LANGUAGE_BY_CODE = Object.freeze(
  Object.fromEntries(LANGUAGE_OPTIONS.map((option) => [option.lang, option])),
)

const POPOVER_GAP_PX = 8
const POPOVER_SAFE_PX = 8
const POPOVER_MAX_WIDTH_PX = 272
const POPOVER_MIN_WIDTH_PX = 224
const POPOVER_EXIT_MS = 230
const FORUM_ANCHOR_MAX_CORRECTION_PX = 520
const FORUM_ANCHOR_MAX_FRAMES = 18

export function computeLanguagePopoverGeometry(anchorRect, viewportWidth) {
  const rightEdge = Math.max(0, Number(anchorRect?.right || 0))
  const bottom = Math.max(0, Number(anchorRect?.bottom || 0))
  const widthBudget = Math.max(POPOVER_MIN_WIDTH_PX, rightEdge - POPOVER_SAFE_PX)
  const width = Math.min(POPOVER_MAX_WIDTH_PX, widthBudget, Math.max(POPOVER_MIN_WIDTH_PX, Number(viewportWidth || 0) - (POPOVER_SAFE_PX * 2)))
  const right = Math.max(0, Number(viewportWidth || 0) - rightEdge)
  return {
    top: Math.round(bottom + POPOVER_GAP_PX),
    right: Math.round(right),
    width: Math.round(width),
  }
}

function readForumScrollHost() {
  try {
    const host = document.querySelector?.('[data-forum-scroll="1"]') || null
    if (host && Number(host.scrollHeight || 0) > Number(host.clientHeight || 0) + 1) return host
  } catch {}
  return null
}

function readForumViewportAnchor() {
  if (typeof window === 'undefined' || typeof document === 'undefined') return null
  try {
    const host = readForumScrollHost()
    const root = host || document
    const hostRect = host?.getBoundingClientRect?.() || null
    const viewportTop = hostRect ? Number(hostRect.top || 0) : 0
    const viewportBottom = hostRect ? Number(hostRect.bottom || 0) : Number(window.innerHeight || 0)
    const cards = [...root.querySelectorAll?.('[data-feed-card="1"]') || []]
    if (!cards.length) return null

    let best = null
    let bestScore = Number.POSITIVE_INFINITY
    for (const node of cards) {
      if (!node?.isConnected || typeof node.getBoundingClientRect !== 'function') continue
      const rect = node.getBoundingClientRect()
      if (Number(rect.bottom || 0) <= viewportTop + 1 || Number(rect.top || 0) >= viewportBottom - 1) continue
      const score = Math.abs(Number(rect.top || 0) - viewportTop)
      if (score < bestScore) {
        best = node
        bestScore = score
      }
    }
    if (!best) return null

    const rect = best.getBoundingClientRect()
    return {
      node: best,
      nodeId: String(best.id || ''),
      top: Number(rect.top || 0),
      host,
      useInner: !!host,
    }
  } catch {
    return null
  }
}

function markLanguageSwitchProgrammaticScroll() {
  try {
    window.__forumProgrammaticScrollTs = Date.now()
    window.__forumProgrammaticScrollReason = 'language_switch_anchor'
  } catch {}
}

function resolveAnchorNode(anchor) {
  if (!anchor) return null
  if (anchor.node?.isConnected) return anchor.node
  if (!anchor.nodeId || typeof document === 'undefined') return null
  try {
    return document.getElementById(anchor.nodeId)
  } catch {
    return null
  }
}

function correctForumViewportAnchor(anchor) {
  if (!anchor || typeof window === 'undefined') return 0
  try {
    const node = resolveAnchorNode(anchor)
    if (!node || typeof node.getBoundingClientRect !== 'function') return 0
    const currentTop = Number(node.getBoundingClientRect().top || 0)
    const delta = currentTop - Number(anchor.top || 0)
    if (!Number.isFinite(delta) || Math.abs(delta) < 0.75) return 0
    if (Math.abs(delta) > FORUM_ANCHOR_MAX_CORRECTION_PX) return 0

    markLanguageSwitchProgrammaticScroll()
    if (anchor.useInner && anchor.host?.isConnected) {
      anchor.host.scrollTop = Math.max(0, Number(anchor.host.scrollTop || 0) + delta)
    } else {
      window.scrollBy?.(0, delta)
    }
    return delta
  } catch {
    return 0
  }
}

function stabilizeForumViewportAnchor(anchor) {
  if (!anchor || typeof window === 'undefined') return () => {}

  let cancelled = false
  let rafId = 0
  let frame = 0
  let stableFrames = 0
  const timers = []

  const stop = () => {
    if (cancelled) return
    cancelled = true
    if (rafId) {
      try { window.cancelAnimationFrame(rafId) } catch {}
      rafId = 0
    }
    for (const timer of timers) {
      try { window.clearTimeout(timer) } catch {}
    }
    window.removeEventListener('wheel', stop, true)
    window.removeEventListener('touchstart', stop, true)
    window.removeEventListener('pointerdown', stop, true)
  }

  const tick = () => {
    if (cancelled) return
    frame += 1
    const delta = correctForumViewportAnchor(anchor)
    stableFrames = Math.abs(delta) < 0.75 ? stableFrames + 1 : 0
    if (stableFrames >= 4 || frame >= FORUM_ANCHOR_MAX_FRAMES) {
      rafId = 0
      return
    }
    rafId = window.requestAnimationFrame(tick)
  }

  window.addEventListener('wheel', stop, { passive: true, capture: true, once: true })
  window.addEventListener('touchstart', stop, { passive: true, capture: true, once: true })
  window.addEventListener('pointerdown', stop, { passive: true, capture: true, once: true })

  rafId = window.requestAnimationFrame(tick)
  timers.push(window.setTimeout(() => { if (!cancelled) correctForumViewportAnchor(anchor) }, 180))
  timers.push(window.setTimeout(() => { if (!cancelled) correctForumViewportAnchor(anchor) }, 480))
  timers.push(window.setTimeout(stop, 900))

  return stop
}

export default function LanguageSwitcher() {
  const { lang, setLang } = useI18n()
  const [portalReady, setPortalReady] = useState(false)
  const [mounted, setMounted] = useState(false)
  const [visible, setVisible] = useState(false)
  const [position, setPosition] = useState({ top: 0, right: 0, width: POPOVER_MAX_WIDTH_PX })
  const [pendingLang, setPendingLang] = useState('')
  const triggerRef = useRef(null)
  const popoverRef = useRef(null)
  const optionRefs = useRef([])
  const closeTimerRef = useRef(0)
  const openRafRef = useRef(0)
  const selectionSeqRef = useRef(0)
  const anchorStopRef = useRef(null)
  const menuId = useId()

  const current = LANGUAGE_BY_CODE[lang] || LANGUAGE_BY_CODE.en

  useEffect(() => {
    setPortalReady(true)
    return () => {
      if (closeTimerRef.current) window.clearTimeout(closeTimerRef.current)
      if (openRafRef.current) window.cancelAnimationFrame(openRafRef.current)
      anchorStopRef.current?.()
      anchorStopRef.current = null
    }
  }, [])

  const updatePosition = useCallback(() => {
    if (typeof window === 'undefined') return
    const rect = triggerRef.current?.getBoundingClientRect?.()
    if (!rect) return
    setPosition(computeLanguagePopoverGeometry(rect, window.innerWidth))
  }, [])

  const openMenu = useCallback(() => {
    if (closeTimerRef.current) {
      window.clearTimeout(closeTimerRef.current)
      closeTimerRef.current = 0
    }
    updatePosition()
    setMounted(true)
    if (openRafRef.current) window.cancelAnimationFrame(openRafRef.current)
    openRafRef.current = window.requestAnimationFrame(() => {
      openRafRef.current = 0
      setVisible(true)
    })
  }, [updatePosition])

  const closeMenu = useCallback(({ returnFocus = false } = {}) => {
    if (!mounted) return
    setVisible(false)
    if (closeTimerRef.current) window.clearTimeout(closeTimerRef.current)
    closeTimerRef.current = window.setTimeout(() => {
      closeTimerRef.current = 0
      setMounted(false)
      if (returnFocus) triggerRef.current?.focus?.({ preventScroll: true })
    }, POPOVER_EXIT_MS)
  }, [mounted])

  const toggleMenu = useCallback(() => {
    if (mounted && visible) closeMenu()
    else openMenu()
  }, [closeMenu, mounted, openMenu, visible])

  useEffect(() => {
    if (!mounted) return undefined

    const onPointerDown = (event) => {
      const target = event?.target
      if (triggerRef.current?.contains?.(target)) return
      if (popoverRef.current?.contains?.(target)) return
      closeMenu()
    }
    const onKeyDown = (event) => {
      if (event.key === 'Escape') {
        event.preventDefault()
        closeMenu({ returnFocus: true })
      }
    }
    const onResize = () => updatePosition()

    document.addEventListener('pointerdown', onPointerDown, true)
    document.addEventListener('keydown', onKeyDown, true)
    window.addEventListener('resize', onResize, { passive: true })
    window.visualViewport?.addEventListener?.('resize', onResize, { passive: true })

    return () => {
      document.removeEventListener('pointerdown', onPointerDown, true)
      document.removeEventListener('keydown', onKeyDown, true)
      window.removeEventListener('resize', onResize)
      window.visualViewport?.removeEventListener?.('resize', onResize)
    }
  }, [closeMenu, mounted, updatePosition])

  const selectLanguage = useCallback(async (nextLang) => {
    const option = LANGUAGE_BY_CODE[nextLang]
    if (!option) return

    const seq = ++selectionSeqRef.current
    closeMenu()
    if (nextLang === lang) return

    setPendingLang(nextLang)
    try { await loadLanguageDict(nextLang) } catch {}
    if (seq !== selectionSeqRef.current) return

    const anchor = readForumViewportAnchor()
    markLanguageSwitchProgrammaticScroll()
    anchorStopRef.current?.()
    anchorStopRef.current = null
    setLang(nextLang)
    anchorStopRef.current = stabilizeForumViewportAnchor(anchor)
    setPendingLang('')
  }, [closeMenu, lang, setLang])

  const onTriggerKeyDown = useCallback((event) => {
    if (event.key === 'Enter' || event.key === ' ' || event.key === 'ArrowDown') {
      event.preventDefault()
      openMenu()
      window.requestAnimationFrame(() => optionRefs.current[0]?.focus?.({ preventScroll: true }))
      return
    }
    if (event.key === 'ArrowUp') {
      event.preventDefault()
      openMenu()
      window.requestAnimationFrame(() => optionRefs.current[LANGUAGE_OPTIONS.length - 1]?.focus?.({ preventScroll: true }))
    }
  }, [openMenu])

  const onOptionKeyDown = useCallback((event, index) => {
    if (event.key !== 'ArrowDown' && event.key !== 'ArrowUp' && event.key !== 'Home' && event.key !== 'End') return
    event.preventDefault()
    let nextIndex = index
    if (event.key === 'ArrowDown') nextIndex = (index + 1) % LANGUAGE_OPTIONS.length
    if (event.key === 'ArrowUp') nextIndex = (index - 1 + LANGUAGE_OPTIONS.length) % LANGUAGE_OPTIONS.length
    if (event.key === 'Home') nextIndex = 0
    if (event.key === 'End') nextIndex = LANGUAGE_OPTIONS.length - 1
    optionRefs.current[nextIndex]?.focus?.({ preventScroll: true })
  }, [])

  const popover = portalReady && mounted ? createPortal(
    <div
      ref={popoverRef}
      id={menuId}
      className={`planet-lang-popover ${visible ? 'is-open' : 'is-closing'}`}
      data-ql7-visual-scope="overlay"
      data-ql7-ios-scroll-raster-exempt="1"
      role="menu"
      aria-label="Language"
      dir="ltr"
      translate="no"
      style={{
        top: `${position.top}px`,
        right: `${position.right}px`,
        width: `${position.width}px`,
      }}
    >
      <span className="planet-lang-popover-aura" aria-hidden="true" />
      <div className="planet-lang-popover-list">
        {LANGUAGE_OPTIONS.map((option, index) => {
          const active = option.lang === lang
          const pending = option.lang === pendingLang
          return (
            <div className="planet-lang-option-wrap" key={option.lang}>
              <button
                ref={(node) => { optionRefs.current[index] = node }}
                type="button"
                className={`planet-lang-option ${active ? 'is-active' : ''} ${pending ? 'is-pending' : ''}`}
                role="menuitemradio"
                aria-checked={active}
                data-lang={option.lang}
                onClick={() => { void selectLanguage(option.lang) }}
                onKeyDown={(event) => onOptionKeyDown(event, index)}
              >
                <span className="planet-lang-option-label" dir={option.dir}>{option.label}</span>
                <span className="planet-lang-option-flag-pill" aria-hidden="true">
                  <span
                    className="planet-lang-option-flag"
                    style={{ backgroundImage: `url(${option.flag})` }}
                  />
                </span>
              </button>
              {index < LANGUAGE_OPTIONS.length - 1 ? (
                <span className="planet-lang-popover-rail" aria-hidden="true"><span /></span>
              ) : null}
            </div>
          )
        })}
      </div>
    </div>,
    document.body,
  ) : null

  return (
    <span className="planet-lang-shell" data-ql7-ios-scroll-raster-exempt="1" translate="no">
      <button
        ref={triggerRef}
        type="button"
        className="planet-lang"
        data-lang={lang}
        title={current.label}
        aria-label={`Language: ${current.label}. Open language menu`}
        aria-haspopup="menu"
        aria-expanded={mounted}
        aria-controls={mounted ? menuId : undefined}
        onClick={toggleMenu}
        onKeyDown={onTriggerKeyDown}
        translate="no"
      >
        <span className="planet-core">
          <span
            className="planet-core-flag"
            style={{ backgroundImage: `url(${current.flag})` }}
            aria-hidden="true"
          />
          <span className="planet-core-label" data-text={current.tag}>
            {current.tag}
          </span>
        </span>
      </button>
      {popover}
    </span>
  )
}
